import { Link, navigate } from "gatsby"
import React from "react"
import styled from "styled-components"
import { Event, track } from "../../analytics"
import { TALKS_PLAYLIST_URL } from "constants/links"
import { PageWidth } from "components/common/Section"

const HeaderContainer = styled.header`
  position: sticky;
  top: 16px;
  z-index: 20;
  width: min(660px, calc(100% - 32px));
  margin: 20px auto 0;
  border: 1px solid ${p => p.theme.border};
  border-radius: 100px;
  background: ${p => p.theme.colors.background.primary[500]}99;
  -webkit-backdrop-filter: blur(8px) saturate(150%);
  backdrop-filter: blur(8px) saturate(150%);
  box-shadow: 0 8px 32px #00000008, inset 0 1px 0 #ffffff55;
  @supports not (backdrop-filter: blur(8px)) {
    background: ${p => p.theme.colors.background.primary[500]}ed;
  }
  @media (prefers-reduced-transparency: reduce) {
    background: ${p => p.theme.colors.background.primary[500]};
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
  }
`
const Bar = styled(PageWidth)`
  width: 100%;
  min-height: 58px;
  padding: 6px 10px 6px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  @media (max-width: 700px) {
    width: 100%;
    min-height: 54px;
  }
`
const Brand = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.025em;
  span:last-child {
    line-height: 1.25;
  }
  @media (max-width: 420px) {
    span:last-child {
      display: none;
    }
  }
`
const Monogram = styled.span`
  width: 38px;
  height: 38px;
  display: none;
  place-items: center;
  border: 1px solid ${p => p.theme.border};
  border-radius: 50%;
  color: ${p => p.theme.colors.accent.primary[500]};
  font-size: 14px;
  letter-spacing: -0.07em;
  @media (max-width: 420px) { display: grid; }
`
const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  @media (max-width: 540px) {
    gap: 4px;
  }
`
const NavLink = styled(Link)`
  font-size: 13px;
  text-decoration: none;
  color: ${p => p.theme.muted};
  padding: 10px 18px;
  border-radius: 100px;
  transition: background 240ms ease, color 240ms ease;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  &:hover,
  &[aria-current="page"] {
    color: ${p => p.theme.colors.text.primary[500]};
    background: ${p => p.theme.surface};
  }
`
const ToggleButton = styled.button`
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${p => p.theme.border};
  background: transparent;
  cursor: pointer;
  &:hover {
    background: ${p => p.theme.surface};
  }
`
const BackButton = styled.button`
  border: 0;
  padding: 10px 0;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: ${p => p.theme.muted};
`
const SkipLink = styled.a`
  position: fixed;
  top: -80px;
  left: 20px;
  padding: 10px 16px;
  background: ${p => p.theme.colors.accent.primary[500]};
  color: ${p => p.theme.accentInk};
  z-index: 100;
  &:focus {
    top: 12px;
  }
`
const Header = ({ toggleTheme, currentTheme, showBackButton, backTo }) => (
  <HeaderContainer>
    <SkipLink href="#main-content">Skip to content</SkipLink>
    <Bar>
      <Brand to="/" aria-label="Chaitanya Deorukhkar — Home">
        <Monogram aria-hidden="true">cd.</Monogram>
        <span>
          Chaitanya Deorukhkar
        </span>
      </Brand>
      <Navigation aria-label="Main navigation">
        {showBackButton ? (
          <BackButton
            onClick={() => {
              track(Event.back_clicked, { to: backTo || "unknown" })
              backTo ? navigate(`/${backTo}`) : window.history.back()
            }}
          >
            ←{" "}
            {backTo ? backTo.charAt(0).toUpperCase() + backTo.slice(1) : "Back"}
          </BackButton>
        ) : (
          <>
            <NavLink
              to="/"
              onClick={() =>
                track(Event.navigation_tab_clicked, { tab: "Home" })
              }
            >
              Home
            </NavLink>
            <NavLink
              as="a"
              href={TALKS_PLAYLIST_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Talks (opens YouTube playlist in a new tab)"
              onClick={() =>
                track(Event.navigation_tab_clicked, { tab: "Talks" })
              }
            >
              Talks <span aria-hidden="true">↗</span>
            </NavLink>
          </>
        )}
        <ToggleButton
          type="button"
          onClick={toggleTheme}
          aria-label={`Switch to ${
            currentTheme === "dark" ? "light" : "dark"
          } theme`}
          title={`Switch to ${
            currentTheme === "dark" ? "light" : "dark"
          } theme`}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            aria-hidden="true"
          >
            {currentTheme === "dark" ? (
              <>
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.4 1.4m11.2 11.2L19 19M5 19l1.4-1.4M17.6 6.4 19 5" />
              </>
            ) : (
              <path d="M20.5 13A8.6 8.6 0 0 1 11 3.5 8.6 8.6 0 1 0 20.5 13Z" />
            )}
          </svg>
        </ToggleButton>
      </Navigation>
    </Bar>
  </HeaderContainer>
)
export default Header
